import React, { useContext, useState } from 'react';

import { useNavigate, Link as RouterLink } from 'react-router-dom';

import { Box, FormControl, FormLabel, InputAdornment, Link, Typography } from '@mui/material';

import { Mail, Lock } from 'lucide-react';

import { LoadingButton } from '../../Components/Loaders';

import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';

import { object, string } from 'yup';

import Axios from './../../Assets/Axios/Axios';

import ToastContext from './../../Context/ToastContext';
import AuthContext from '../../Context/AuthContext';

import { COLORS } from '../../Constant/COLORS';
import FONTS from '../../Constant/FONTS';

import Layout from './Components/Layout';
import TextFieldGrey from './Components/TextFieldGrey';
import PasswordField from '../../Components/PasswordField';

import { localStorageData, LOGOUT } from '../../lib/Token';
import FireBase from '../../lib/fireBaseProfessional';
import { signOutFirebaseUser } from '../../lib/firebaseClients';

import { getClearedErrorObj } from '../../Helpers/index';

import { FindProfessionalByFbUid } from '../../Server/Query';
import { PROFESSIONAL_STATUS, USER_TYPES } from '../../Constant/Enums';

const Login = () => {
  const { setUser } = useContext(AuthContext);
  const { showToastMessage } = useContext(ToastContext);

  const [inputs, setInputs] = useState({ email: '', password: '' });
  const [error, setError] = useState({ email: null, password: null });
  const [isRequestAgainIsVisible, setIsRequestAgainIsVisible] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  const handleChange = event => {
    const { name, value } = event.target;
    if (name === 'phoneNumber' && !/^\+\d{0,11}$/.test(value) && value !== '') return;
    setInputs(prevState => ({ ...prevState, [name]: value }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(prevErrors => getClearedErrorObj(prevErrors));
    try {
      const validatedData = await schema.validate(inputs, {
        abortEarly: false,
      });

      // 1) Firebase sign-in first. Never leak which emails are registered.
      const firebaseResponse = await FireBase.signIn(
        validatedData.email,
        validatedData.password
      );

      if (firebaseResponse.errors) {
        switch (firebaseResponse.errors) {
          case 'auth/wrong-password':
          case 'auth/invalid-credential':
          case 'auth/user-not-found':
            setError(current => ({
              ...current,
              email: 'Email or password is incorrect.',
            }));
            return undefined;
          case 'auth/user-disabled':
            showToastMessage('Your account has been disabled', 'info');
            return undefined;
          default:
            showToastMessage('Something went wrong while trying to login.');
            return undefined;
        }
      }

      const fbUid = firebaseResponse.data?.uid;

      // 2) Mark session so Axios attaches the professional-project ID token.
      localStorageData({ type: USER_TYPES.PROFESSIONAL });

      // 3) Resolve the DB row by Firebase UID.
      const responseDB = await Axios(FindProfessionalByFbUid({ fbUid }));

      if (responseDB?.errors || !responseDB?.data?.Professional) {
        await signOutFirebaseUser(USER_TYPES.PROFESSIONAL).catch(() => undefined);
        LOGOUT();
        setError(current => ({
          ...current,
          email: 'Email or password is incorrect.',
        }));
        return undefined;
      }

      const { id, status, isActive } = responseDB.data.Professional;

      // Pending (REQUESTED) and REJECTED applicants are inactive by design — route
      // them to the status page ("our team will contact you" / rejected) FIRST, so
      // they aren't mistaken for an approved account an admin later disabled.
      if (
        status === PROFESSIONAL_STATUS.REQUESTED ||
        status === PROFESSIONAL_STATUS.REJECTED
      ) {
        // Don't promote them to a logged-in professional in our local context.
        await signOutFirebaseUser(USER_TYPES.PROFESSIONAL).catch(() => undefined);
        LOGOUT();
        navigate('/professional/status', { state: { status } });
        return undefined;
      }

      // Approved account that an admin has since deactivated.
      if (!isActive) {
        await signOutFirebaseUser(USER_TYPES.PROFESSIONAL).catch(() => undefined);
        LOGOUT();
        showToastMessage('Your account has been disabled', 'info');
        return undefined;
      }

      const user = {
        userID: id,
        type: USER_TYPES.PROFESSIONAL,
      };
      setUser(user);
      localStorageData(user);

      showToastMessage('Hey there, welcome back', 'success');
      navigate('/professional/dashboard/my-details');
    } catch (error) {
      if (error.name !== 'ValidationError') {
        console.error(error);
      } else {
        const [validationError] = error.inner;
        setError(prevErrors => ({
          ...prevErrors,
          [validationError.path]: validationError.message,
        }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout
      gap={{ xs: 1.5, md: 3 }}
      px={{ xs: 2, md: 8 }}
      py={{ xs: 2, md: 5 }}
      my={2}
      width={{ xs: 9 / 10, md: 4 / 10 }}
      maxWidth={{ xs: '400px', md: 'unset' }}
      component='form'
      onSubmit={handleSubmit}>
      <PersonOutlineOutlinedIcon
        sx={{
          backgroundColor: COLORS.teal,
          borderRadius: '100%',
          color: 'white',
          fontSize: 63,
          padding: 1,
          mx: 'auto',
        }}
      />

      <Box textAlign='center'>
        <Typography variant='h4' mb={0.5}>
          Welcome back
        </Typography>
        <Typography color={COLORS.inkSoft}>
          Sign in to your professional account
        </Typography>
      </Box>

      <FormControl fullWidth>
        <FormLabel>
          <Typography color='black' fontWeight={500} mb={1}>
            Email
          </Typography>
        </FormLabel>
        <TextFieldGrey
          spellCheck={false}
          autoComplete='email'
          placeholder='john@gmail.com'
          value={inputs.email}
          name='email'
          onChange={handleChange}
          error={Boolean(error.email)}
          helperText={error.email}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Mail size={18} color={COLORS.inkSoft} strokeWidth={1.8} />
              </InputAdornment>
            ),
          }}
        />
      </FormControl>

      <FormControl fullWidth>
        <FormLabel>
          <Typography color='black' fontWeight={500} mb={1}>
            Password
          </Typography>
        </FormLabel>
        <PasswordField
          autoComplete='current-password'
          error={Boolean(error.password)}
          helperText={error.password}
          value={inputs.password}
          name='password'
          onChange={handleChange}
          placeholder='*********'
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Lock size={18} color={COLORS.inkSoft} strokeWidth={1.8} />
              </InputAdornment>
            ),
            sx: {
              fontFamily: FONTS.MONT,
              backgroundColor: COLORS.surfaceAlt,
            },
          }}
        />
      </FormControl>

      <Link
        variant='caption'
        fontWeight={500}
        textAlign='center'
        component={RouterLink}
        to='../forgot-password'>
        Forgot your password?
      </Link>

      <LoadingButton
        type='submit'
        variant='outlined'
        color='blue'
        isLoading={isSubmitting}
        sx={{
          width: 'fit-content',
          mx: 'auto',
          color: COLORS.blue,
          fontWeight: 500,
          px: 3,
        }}>
        Login
      </LoadingButton>
    </Layout>
  );
};

const schema = object({
  email: string().trim().required().email().label('Email'),
  password: string().trim().required().label('Password'),
});

export default Login;
